import React from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NavBar from '../components/navbar';
import Footer from '../components/footer';
import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import CtaSection from '../components/CtaSection';
import ScrollReveal from '../components/animated/ScrollReveal';
import Text from '../components/Text';
import MagneticButton from '../components/animated/MagneticButton';
import { colors, gradients } from '../theme/designTokens';
import { technologyFeatures } from '../data/insights';
import { useHashScroll } from '../hooks/useHashScroll';

const platforms = [
  {
    id: 'qrify',
    name: 'QRify',
    description:
      'Accelerated Emergency Response—QR-enabled intervention that bridges incident to care when every second counts.',
    href: 'https://svasth.ai/product',
  },
  {
    id: 'svasth',
    name: 'Svasth.ai',
    description:
      'Early Warning Solution—AI-driven health intelligence, real-time alerts, and coordinated care for workforce resilience.',
    href: 'https://svasth.ai/',
  },
  {
    id: 'monitoring',
    name: 'Risk Engines & Monitoring',
    description:
      'Analytics, dashboards, early warning systems, and real-time monitoring that power AERA™ Anticipate and Advance.',
  },
];

const claimsResiliencePhases = [
  {
    number: '01',
    title: 'Pre-Loss Readiness',
    summary: 'Build claims readiness before disruption hits.',
    items: [
      'Claims protocols',
      'Document readiness',
      'Policy analysis',
      'Incident reporting procedures',
      'Training',
      'Mock claim exercises',
    ],
  },
  {
    number: '02',
    title: 'Active Claim Management',
    summary: 'End-to-end support from first notice through settlement.',
    items: [
      'First notification of loss (FNOL)',
      'Documentation',
      'Insurer coordination',
      'Survey support',
      'Legal and financial claim management',
      'Negotiation and settlement',
    ],
  },
  {
    number: '03',
    title: 'Post-Loss Resilience',
    summary: 'Turn claim experience into stronger resilience.',
    items: [
      'Root cause analysis',
      'Recovery actions',
      'Coverage optimization',
      'Control enhancements',
      'Business continuity improvements',
      'Renewal strategy informed by claim experience',
    ],
  },
];

const Technology = () => {
  const navigate = useNavigate();
  useHashScroll();

  return (
  <Box sx={{ bgcolor: colors.white, overflow: 'hidden' }}>
    <NavBar />
    <PageHero
      variant="dark"
      eyebrow="Technology"
      title="Technology Enabled"
      titleHighlight="Resilience."
      subtitle="AI, analytics, dashboards, prescriptive intelligence, and proprietary platforms—built to make resilience measurable."
      badges={['AI', 'QRify', 'Svasth.ai', 'Monitoring']}
    />

    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 12 } }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <ScrollReveal>
          <SectionHeader
            eyebrow="Platforms"
            title="Differentiated by technology."
            highlightWords={['technology']}
            subtitle="Technology is not a brochure feature—it is how ALEgION operationalizes resilience engineering."
          />
        </ScrollReveal>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
            mb: 8,
          }}
        >
          {platforms.map((p) => (
            <Box
              key={p.name}
              id={p.id}
              component={p.href ? 'a' : 'div'}
              href={p.href}
              target={p.href ? '_blank' : undefined}
              rel={p.href ? 'noopener noreferrer' : undefined}
              sx={{
                p: 4,
                background: gradients.sectionDark,
                minHeight: 220,
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                cursor: p.href ? 'pointer' : 'default',
                scrollMarginTop: 96,
                transition: 'background 0.25s ease',
                ...(p.href
                  ? {
                      '&:hover': {
                        background: 'linear-gradient(165deg, #020617 0%, #0F172A 48%, #243447 100%)',
                      },
                    }
                  : {}),
              }}
            >
              <Text variant="h3" sx={{ color: colors.ochreLight, mb: 2 }}>
                {p.name}
              </Text>
              <Text variant="body" color="muted">
                {p.description}
              </Text>
            </Box>
          ))}
        </Box>

        <ScrollReveal>
          <SectionHeader
            eyebrow="Capabilities"
            title="The technology stack behind AERA™."
            highlightWords={['AERA™']}
          />
        </ScrollReveal>
        <Box
          id="ai"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
            scrollMarginTop: 96,
          }}
        >
          {technologyFeatures.map((f) => {
            const isExternal = Boolean(f.href && f.href.startsWith('http'));
            return (
            <Box
              key={f.title}
              component={f.href ? 'a' : 'div'}
              href={f.href}
              target={isExternal ? '_blank' : undefined}
              rel={isExternal ? 'noopener noreferrer' : undefined}
              sx={{
                p: 3,
                border: `1px solid ${colors.borderLight}`,
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                cursor: f.href ? 'pointer' : 'default',
                transition: 'border-color 0.25s ease, background 0.25s ease',
                ...(f.href
                  ? {
                      '&:hover': {
                        borderColor: 'rgba(212,160,23,0.55)',
                        background: 'rgba(212,160,23,0.04)',
                      },
                    }
                  : {}),
              }}
            >
              <Text variant="h4" color="primary" sx={{ mb: 1 }}>
                {f.title}
              </Text>
              <Text variant="bodySm" color="secondary">
                {f.description}
              </Text>
            </Box>
            );
          })}
        </Box>

        <ScrollReveal>
          <Box
            sx={{
              mt: 8,
              p: { xs: 3, md: 4 },
              border: `1px solid ${colors.borderLight}`,
              background: colors.offWhite,
              textAlign: 'center',
            }}
          >
            <Text variant="h3" color="primary" sx={{ mb: 1 }}>
              Tools on the horizon
            </Text>
            <Text variant="body" color="secondary" sx={{ mb: 3, display: 'block', maxWidth: 560, mx: 'auto' }}>
              Cyber Exposure Score, Climate Risk Dashboard, Insurance Optimization Calculator, and AI Chat Assistant—entry points available; full products coming soon.
            </Text>
            <MagneticButton variant="navy" onClick={() => navigate('/contact')}>
              Request Early Access
            </MagneticButton>
          </Box>
        </ScrollReveal>
      </Box>
    </Box>

    <Box
      id="claims-resilience-framework"
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 8, md: 12 },
        background: gradients.sectionDark,
        scrollMarginTop: 96,
      }}
    >
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <ScrollReveal>
          <SectionHeader
            eyebrow="Claims Resilience Framework"
            title="Beyond a claims manual."
            highlightWords={['claims']}
            subtitle="As a Resilience Enabler, ALEgION expands claims into three interconnected phases—preparing before loss, managing through the claim, and strengthening after it."
            light
          />
        </ScrollReveal>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {claimsResiliencePhases.map((phase) => (
            <Box
              key={phase.title}
              sx={{
                p: { xs: 3, md: 3.5 },
                border: `1px solid ${colors.glassBorder}`,
                background: 'rgba(255,255,255,0.03)',
                minHeight: { md: 360 },
              }}
            >
              <Text
                variant="eyebrow"
                sx={{ color: colors.ochre, mb: 1.5, display: 'block' }}
              >
                {phase.number}
              </Text>
              <Text variant="h3" color="white" sx={{ mb: 1.5 }}>
                {phase.title}
              </Text>
              <Text
                variant="bodySm"
                color="muted"
                sx={{ mb: 3, display: 'block' }}
              >
                {phase.summary}
              </Text>
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                {phase.items.map((item) => (
                  <Box
                    component="li"
                    key={item}
                    sx={{
                      mb: 1.25,
                      color: 'rgba(255,255,255,0.78)',
                      fontSize: '0.9rem',
                      lineHeight: 1.55,
                      '&::marker': { color: colors.ochre },
                    }}
                  >
                    {item}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    <CtaSection secondaryPath="/framework" secondaryLabel="See AERA™" />
    <Footer />
  </Box>
  );
};

export default Technology;
